import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useTranslation } from 'react-i18next';
import { PiHandCoinsLight } from 'react-icons/pi';
import { IRepaymentPopupForm } from 'services/advance/types';
import { getDefaultCurrency } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { number, object } from 'yup';
import PopupForm, { IPopupFormProps } from '../form/PopupForm';

interface IRepaymentPopupProps
  extends Omit<
    IPopupFormProps<IRepaymentPopupForm>,
    'initialValues' | 'header'
  > {
  name?: string;
  maxRepaymentValue?: number;
}

const RepaymentPopup = ({
  maxRepaymentValue,
  name,
  ...props
}: IRepaymentPopupProps) => {
  const { t } = useTranslation();
  const defaultCurrency = getDefaultCurrency()?.currencyCode ?? '';
  return (
    <PopupForm<IRepaymentPopupForm>
      {...props}
      header={
        <>
          <PiHandCoinsLight /> {t('button.repay')} {name ?? ''}
        </>
      }
      validationSchema={object().shape({
        amount: number().when('totalRepayment', {
          is: (totalRepayment: boolean) => !totalRepayment,
          then: schema =>
            schema
              .moreThan(
                0,
                t('formik-error.min-suffix-value', {
                  suffix: defaultCurrency,
                  value: 0,
                })
              )
              .max(
                maxRepaymentValue ?? 999999999999,
                t('formik-error.max-suffix-value', {
                  suffix: defaultCurrency,
                  value: maxRepaymentValue ?? 999999999999,
                })
              )
              .required(t('formik-error.required')),
          otherwise: schema => schema.optional().nullable(),
        }),
      })}
      initialValues={{
        totalRepayment: true,
        amount: 0,
      }}
      submitButton={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.repay')} />
        </Button>
      }>
      {({ values }) => (
        <>
          <FormInput.Select
            name='totalRepayment'
            label={t('input.label.totalRepayment')}
            isClearable={false}
            options={[
              { label: t('input.option.yes'), value: true },
              { label: t('input.option.no'), value: false },
            ]}
          />
          <FormInput.Number
            name='amount'
            label={t('input.label.amount')}
            placeholder={`0 ${defaultCurrency}`}
            suffix={` ${defaultCurrency}`}
            disabled={values.totalRepayment}
          />
        </>
      )}
    </PopupForm>
  );
};

export default RepaymentPopup;
